import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  authActions,
  hasAnyRole,
  selectAuth,
  selectAuthRoles,
  type AdminRoleCode,
} from '@store/slices/auth';
import { firebaseAuth } from '@config/firebase';
import { authLog } from '@utils/authLog';
import {
  isLoginInFlight,
  loadMe,
  loginWithEmail,
  logout,
  subscribeToAuthChanges,
  waitForAuthReady,
} from './api';
import type { LoginFormValues } from './schema';

export function useAuth() {
  return useAppSelector(selectAuth);
}

export function useRoles(): readonly AdminRoleCode[] {
  return useAppSelector(selectAuthRoles);
}

export function useHasAnyRole(...allowed: AdminRoleCode[]): boolean {
  const roles = useAppSelector(selectAuthRoles);
  return hasAnyRole(roles, allowed);
}

export function useLogin() {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationFn: (values: LoginFormValues) => loginWithEmail(values),
    onSuccess: ({ user, token, profile, me }) => {
      dispatch(authActions.setSession({ user, token, profile }));
      dispatch(authActions.setRoles(me.roles));
    },
  });
}

export function useLogout() {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => dispatch(authActions.clearSession()),
    onError: () => dispatch(authActions.clearSession()),
  });
}

/**
 * Boot sequence on every app load:
 *   1. Wait for Firebase to finish loading its persisted session from storage.
 *   2. If a user is persisted: get a fresh ID token, then call `/me` on
 *      commodity-mdm-api to load roles. Dispatch session + roles together.
 *   3. If `/me` rejects (401), tear down — the user's admin-panel access was
 *      revoked between sessions, so the persisted Firebase session is dead
 *      weight. Bounce them to login.
 *   4. Flip `initializing` to false so route guards stop showing the loader.
 *   5. Subscribe to future Firebase auth changes (token refresh, sign-out).
 *      Token refreshes only update user/token — roles do not change unless
 *      the user re-authenticates.
 *
 * The transient `null` events that Firebase emits during persistence
 * rehydration and during `signInWithCustomToken` are filtered: rehydration
 * is avoided by waiting for `authStateReady()`; the custom-token transient
 * is filtered by the `loginInFlight` guard.
 */
export function useAuthSubscription() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      authLog('boot-start');
      await waitForAuthReady();
      if (cancelled) return;

      const current = firebaseAuth.currentUser;
      if (current) {
        try {
          const token = await current.getIdToken();
          dispatch(
            authActions.setSession({
              user: {
                uid: current.uid,
                email: current.email,
                displayName: current.displayName,
                photoURL: current.photoURL,
              },
              token,
            }),
          );
          // /me needs `apiClient` to read the token from `firebaseAuth.currentUser`,
          // which it already does via the request interceptor.
          const me = await loadMe();
          if (cancelled) return;
          dispatch(authActions.setRoles(me.roles));
          authLog('session-set', {
            reason: 'rehydrate',
            uid: current.uid,
            roles: me.roles,
          });
        } catch (err) {
          // Either the token fetch failed (revoked/network) or /me rejected
          // the session (admin access revoked). In both cases we cannot
          // continue — sign out so the next render shows /login.
          await signOut(firebaseAuth).catch(() => {});
          dispatch(authActions.clearSession());
          authLog('session-cleared', {
            reason: 'rehydrate-failed',
            error: err instanceof Error ? err.message : String(err),
          });
        }
      } else {
        dispatch(authActions.clearSession());
        authLog('session-cleared', { reason: 'no-firebase-user' });
      }
      dispatch(authActions.setInitializing(false));
      authLog('initializing-false');

      unsub = subscribeToAuthChanges((user, token) => {
        if (user && token) {
          // Suppress positive session-commits while a login is in flight.
          // `loginWithEmail` signs into Firebase in step 1 (which fires this
          // callback) before the backend has confirmed admin-panel access in
          // later steps. Committing here would flip `isAuthed` true and bounce
          // the user to /home prematurely — then if a later step fails (e.g.
          // a mobile-only account rejected by /me) the session is torn down,
          // leaving a blank page instead of the login error. `useLogin`'s
          // onSuccess is the sole authority that commits a fresh login session.
          if (isLoginInFlight()) {
            authLog('subscribe-set-suppressed', {
              uid: user.uid,
              reason: 'login-in-flight',
            });
            return;
          }
          // Token refresh only — preserve existing roles. /me is NOT re-fetched
          // here; roles only change when the user signs out and back in.
          dispatch(authActions.setSession({ user, token }));
          authLog('subscribe-set', { uid: user.uid });
          return;
        }

        // Safety net: Firebase still says the user is signed in, but our
        // callback got null (likely a transient `getIdToken()` failure during
        // boot). Don't tear down the session — wait for the next event.
        if (firebaseAuth.currentUser && !isLoginInFlight()) {
          authLog('subscribe-transient-null', {
            uid: firebaseAuth.currentUser.uid,
          });
          return;
        }

        if (!isLoginInFlight()) {
          dispatch(authActions.clearSession());
          authLog('subscribe-clear', { reason: 'genuine-signout' });
        } else {
          authLog('subscribe-clear-suppressed', { reason: 'login-in-flight' });
        }
      });
    })();

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [dispatch]);
}
